import { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Loader2, Download, FileText, BarChart3, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { authGet } from "@/utils/auth";

const inr = (n) => (n === null || n === undefined || n === "" ? "—" : "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));
const inrShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return "₹" + (v / 1e7).toFixed(2) + "Cr";
  if (Math.abs(v) >= 1e5) return "₹" + (v / 1e5).toFixed(2) + "L";
  if (Math.abs(v) >= 1e3) return "₹" + (v / 1e3).toFixed(1) + "K";
  return "₹" + v;
};
const PALETTE = ["#1B2A4A", "#A87608", "#10b981", "#3b82f6", "#ef4444", "#8b5cf6", "#f59e0b", "#0ea5e9"];
const SOURCES = ["Direct", "Beshak", "Associate"];
const cardCls = "bg-white/60 border border-[#C9D4E6] rounded-2xl";

const ChartCard = ({ title, children, tid }) => (
  <div className={`${cardCls} p-4`} data-testid={tid}>
    <p className="text-[#1B2A4A] font-semibold text-sm mb-3">{title}</p>
    {children}
  </div>
);

const RevenueAnalytics = ({ channels = [] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ fy: "all", channel: "all", source: "all" });

  const load = useCallback(async () => {
    setLoading(true);
    const qs = Object.entries(filters).filter(([, v]) => v && v !== "all").map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    const r = await authGet(`/api/advisor/revenue/analytics${qs ? "?" + qs : ""}`);
    if (r.ok) setData(r.data);
    else toast.error("Could not load analytics");
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const filterLabel = () => {
    const bits = [];
    if (filters.fy !== "all") bits.push(filters.fy);
    if (filters.channel !== "all") bits.push(filters.channel);
    if (filters.source !== "all") bits.push(filters.source);
    return bits.length ? bits.join(" · ") : "All time · All channels · All sources";
  };

  const downloadBlob = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [];
    rows.push(["Kavach Box — Revenue Report"]);
    rows.push(["Filter", filterLabel()]);
    rows.push(["Generated", new Date().toLocaleString("en-IN")]);
    rows.push([]);
    rows.push(["TOTALS"]);
    rows.push(["Gross commission", t.gross]);
    rows.push(["Net revenue", t.net]);
    rows.push(["Referral total", t.referral_total]);
    rows.push(["Referral paid", t.referral_paid]);
    rows.push(["Referral payable", t.referral_payable]);
    rows.push(["Records", t.record_count]);
    rows.push([]);
    rows.push(["BY FINANCIAL YEAR", "Gross", "Net", "Records"]);
    (data.by_fy || []).forEach((x) => rows.push([x.fy, x.gross, x.net, x.count]));
    rows.push([]);
    rows.push(["BY CHANNEL", "Gross", "Net", "Records"]);
    (data.by_channel || []).forEach((x) => rows.push([x.channel, x.gross, x.net, x.count]));
    rows.push([]);
    rows.push(["BY SOURCE", "Gross", "Net", "Records"]);
    (data.by_source || []).forEach((x) => rows.push([x.source, x.gross, x.net, x.count]));
    rows.push([]);
    rows.push(["BY ENTRY TYPE", "Gross", "Records"]);
    (data.by_entry_type || []).forEach((x) => rows.push([x.entry_type, x.gross, x.count]));
    rows.push([]);
    rows.push(["TOP INSURERS", "Gross", "Records"]);
    (data.by_insurer || []).forEach((x) => rows.push([x.insurer, x.gross, x.count]));
    rows.push([]);
    rows.push(["REFERRAL BY ASSOCIATE", "Total", "Paid", "Payable", "Records"]);
    (data.by_associate || []).forEach((x) => rows.push([x.associate, x.referral_total, x.paid, x.payable, x.count]));
    rows.push([]);
    rows.push(["OPEN ISSUES", "Client", "Policy No", "Insurer", "Channel", "Commission", "Status"]);
    (data.open_issues?.records || []).forEach((x) => rows.push([x.record_id, x.client, x.policy_no, x.insurer, x.channel, x.total_commission, x.status]));
    const csv = rows.map((r) => r.map((c) => {
      const s = c === null || c === undefined ? "" : String(c);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    downloadBlob("\ufeff" + csv, `kavachbox-revenue-${Date.now()}.csv`, "text/csv;charset=utf-8;");
    toast.success("CSV downloaded");
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const gold = [168, 118, 8]; const navy = [27, 42, 74];
    doc.setFillColor(...navy); doc.rect(0, 0, 210, 22, "F");
    doc.setTextColor(255, 255, 255); doc.setFontSize(16); doc.setFont(undefined, "bold");
    doc.text("Kavach Box — Revenue Report", 14, 14);
    doc.setTextColor(60, 60, 60); doc.setFontSize(9); doc.setFont(undefined, "normal");
    doc.text(`Filter: ${filterLabel()}`, 14, 29);
    doc.text(`Generated: ${new Date().toLocaleString("en-IN")}`, 14, 34);

    autoTable(doc, {
      startY: 40,
      head: [["Metric", "Amount"]],
      body: [
        ["Gross commission", inr(t.gross)],
        ["Net revenue", inr(t.net)],
        ["Referral total", inr(t.referral_total)],
        ["Referral paid", inr(t.referral_paid)],
        ["Referral payable", inr(t.referral_payable)],
        ["Records", String(t.record_count ?? 0)],
      ],
      theme: "grid", headStyles: { fillColor: gold }, styles: { fontSize: 9 },
    });

    const section = (title, head, body) => {
      if (!body.length) return;
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 6,
        head: [[title]], body: [], theme: "plain",
        headStyles: { fillColor: [237, 241, 248], textColor: navy, fontStyle: "bold", fontSize: 11 },
      });
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 1,
        head: [head], body, theme: "striped", headStyles: { fillColor: navy }, styles: { fontSize: 8 },
      });
    };

    section("By Financial Year", ["FY", "Gross", "Net", "Records"],
      (data.by_fy || []).map((x) => [x.fy, inr(x.gross), inr(x.net), x.count]));
    section("By Channel", ["Channel", "Gross", "Net", "Records"],
      (data.by_channel || []).map((x) => [x.channel, inr(x.gross), inr(x.net), x.count]));
    section("By Source", ["Source", "Gross", "Net", "Records"],
      (data.by_source || []).map((x) => [x.source, inr(x.gross), inr(x.net), x.count]));
    section("Top Insurers", ["Insurer", "Gross", "Records"],
      (data.by_insurer || []).map((x) => [x.insurer, inr(x.gross), x.count]));
    section("Referral by Associate", ["Associate", "Total", "Paid", "Payable", "Records"],
      (data.by_associate || []).map((x) => [x.associate, inr(x.referral_total), inr(x.paid), inr(x.payable), x.count]));
    section("Open Issues", ["ID", "Client", "Policy", "Channel", "Commission", "Status"],
      (data.open_issues?.records || []).map((x) => [x.record_id, x.client, x.policy_no, x.channel, inr(x.total_commission), x.status]));

    doc.save(`kavachbox-revenue-${Date.now()}.pdf`);
    toast.success("PDF downloaded");
  };

  if (loading) return <div className="p-10 text-center text-[#3A4557]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;
  if (!data) return null;

  const empty = t.record_count === 0;
  const fyChart = (data.by_fy || []).filter((x) => x.fy !== "Undated");

  return (
    <div className="space-y-4" data-testid="revenue-analytics">
      {/* Filters + export */}
      <div className="flex flex-wrap gap-2 items-center">
        <Select value={filters.fy} onValueChange={(v) => setFilters((f) => ({ ...f, fy: v }))}>
          <SelectTrigger className="w-auto min-w-[130px] bg-white h-9 text-sm" data-testid="analytics-filter-fy"><SelectValue placeholder="FY" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All financial years</SelectItem>{(data.fy_options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.channel} onValueChange={(v) => setFilters((f) => ({ ...f, channel: v }))}>
          <SelectTrigger className="w-auto min-w-[130px] bg-white h-9 text-sm" data-testid="analytics-filter-channel"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All channels</SelectItem>{channels.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.source} onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}>
          <SelectTrigger className="w-auto min-w-[120px] bg-white h-9 text-sm" data-testid="analytics-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sources</SelectItem>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button onClick={exportCSV} variant="outline" className="border-[#C9D4E6] h-9 text-sm" data-testid="export-csv-btn"><Download className="h-4 w-4 mr-1" /> CSV / Excel</Button>
          <Button onClick={exportPDF} className="bg-[#1B2A4A] hover:bg-[#0D1A3A] text-white h-9 text-sm" data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
        </div>
      </div>

      {empty ? (
        <div className={`${cardCls} p-10 text-center text-[#3A4557]`}><BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />No revenue in this view yet. Import records or clear the filters.</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { label: "Gross Commission", val: t.gross, color: "text-[#1B2A4A]", tid: "kpi-gross" },
              { label: "Net Revenue", val: t.net, color: "text-emerald-600", tid: "kpi-net" },
              { label: "Referral Payable", val: t.referral_payable, color: "text-amber-600", tid: "kpi-payable" },
              { label: "Records", val: t.record_count, isCount: true, color: "text-[#1B2A4A]", tid: "kpi-records" },
            ].map((c) => (
              <div key={c.label} className={`${cardCls} p-4`} data-testid={c.tid}>
                <p className="text-[#3A4557] text-xs">{c.label}</p>
                <p className={`text-xl font-bold mt-1 ${c.color}`}>{c.isCount ? c.val : inr(c.val)}</p>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <ChartCard title="Gross vs Net by financial year" tid="chart-fy">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={fyChart} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E9F4" />
                  <XAxis dataKey="fy" tick={{ fontSize: 11, fill: "#3A4557" }} />
                  <YAxis tickFormatter={inrShort} tick={{ fontSize: 11, fill: "#3A4557" }} width={54} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="gross" name="Gross" fill="#1B2A4A" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="net" name="Net" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Gross commission by channel" tid="chart-channel">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={data.by_channel} dataKey="gross" nameKey="channel" cx="50%" cy="50%" outerRadius={90} label={(e) => e.channel}>
                    {(data.by_channel || []).map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Gross by source" tid="chart-source">
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={data.by_source} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E9F4" />
                  <XAxis type="number" tickFormatter={inrShort} tick={{ fontSize: 11, fill: "#3A4557" }} />
                  <YAxis type="category" dataKey="source" tick={{ fontSize: 11, fill: "#3A4557" }} width={70} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="gross" name="Gross" fill="#A87608" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Gross by entry type" tid="chart-entry">
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={data.by_entry_type} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E9F4" />
                  <XAxis type="number" tickFormatter={inrShort} tick={{ fontSize: 11, fill: "#3A4557" }} />
                  <YAxis type="category" dataKey="entry_type" tick={{ fontSize: 10, fill: "#3A4557" }} width={110} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="gross" name="Gross" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className={`${cardCls} p-4`} data-testid="table-insurers">
              <p className="text-[#1B2A4A] font-semibold text-sm mb-2">Top insurers</p>
              <table className="w-full text-sm">
                <thead className="text-[#3A4557] text-xs"><tr><th className="text-left p-1">Insurer</th><th className="text-right p-1">Gross</th><th className="text-right p-1">Records</th></tr></thead>
                <tbody>
                  {(data.by_insurer || []).length === 0 ? <tr><td colSpan={3} className="p-3 text-center text-[#3A4557] text-xs">No data</td></tr> :
                    data.by_insurer.map((x) => <tr key={x.insurer} className="border-t border-[#C9D4E6]"><td className="p-1">{x.insurer}</td><td className="p-1 text-right font-medium">{inr(x.gross)}</td><td className="p-1 text-right">{x.count}</td></tr>)}
                </tbody>
              </table>
            </div>

            <div className={`${cardCls} p-4`} data-testid="table-associates">
              <p className="text-[#1B2A4A] font-semibold text-sm mb-2">Referral by associate</p>
              <table className="w-full text-sm">
                <thead className="text-[#3A4557] text-xs"><tr><th className="text-left p-1">Associate</th><th className="text-right p-1">Total</th><th className="text-right p-1">Paid</th><th className="text-right p-1">Payable</th></tr></thead>
                <tbody>
                  {(data.by_associate || []).length === 0 ? <tr><td colSpan={4} className="p-3 text-center text-[#3A4557] text-xs">No referral splits yet</td></tr> :
                    data.by_associate.map((x) => <tr key={x.associate} className="border-t border-[#C9D4E6]"><td className="p-1">{x.associate}</td><td className="p-1 text-right">{inr(x.referral_total)}</td><td className="p-1 text-right text-emerald-700">{inr(x.paid)}</td><td className="p-1 text-right text-amber-700 font-medium">{inr(x.payable)}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Open issues */}
          <div className={`${cardCls} p-4`} data-testid="table-issues">
            <div className="text-[#1B2A4A] font-semibold text-sm mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" /> Open issues
              <Badge className="bg-red-100 text-red-700 border-red-300">{data.open_issues?.count || 0} · {inr(data.open_issues?.amount)}</Badge>
            </div>
            {(data.open_issues?.records || []).length === 0 ? (
              <p className="text-[#3A4557] text-xs py-2">No open issues — all records are CLEAN.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="text-[#3A4557] text-xs bg-[#E2E9F4]"><tr>{["ID", "Client", "Policy No", "Insurer", "Channel", "Commission", "Status"].map((h) => <th key={h} className="text-left p-2">{h}</th>)}</tr></thead>
                  <tbody>
                    {data.open_issues.records.map((x) => (
                      <tr key={x.record_id} className="border-t border-[#C9D4E6]">
                        <td className="p-2 font-mono text-xs">{x.record_id}</td>
                        <td className="p-2">{x.client || "—"}</td>
                        <td className="p-2 font-mono text-xs">{x.policy_no || "—"}</td>
                        <td className="p-2">{x.insurer || "—"}</td>
                        <td className="p-2">{x.channel}</td>
                        <td className="p-2 font-medium">{inr(x.total_commission)}</td>
                        <td className="p-2"><Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">{x.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RevenueAnalytics;
